import type { EventBinding } from "../api/events";

const UUID =
  /^[0-9a-f]{8}-[0-9a-f]{4}-7[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
const KEY = /^event:[a-z0-9]+:[0-9a-f]{36}$/;
export const EVENT_OPERATION_TYPES = [
  "sourcing.invitation.create",
  "sourcing.invitation.reinvite",
  "sourcing.invitation.resend",
  "sourcing.participation.revoke",
  "sourcing.clarification.message",
  "sourcing.reopen",
  "sourcing.round.start",
  "opening.request",
  "opening.confirm",
  "qualification.record",
  "auction.rules.accept",
  "auction.step.accept",
  "auction.bid.submit",
  "auction.lot.bid.submit",
  "auction.document.prepare",
  "auction.document.freeze",
  "supplier.event.accept",
  "supplier.event.decline",
  "supplier.event.acknowledge",
  "supplier.event.message",
] as const;
export type EventOperationType = (typeof EVENT_OPERATION_TYPES)[number];
export interface EventScope {
  eventId: string;
  participationId?: string;
  grantId?: string;
  revisionId?: string;
  stageCode?: string;
  openingId?: string;
  expectedVersion?: string;
  roundNumber?: string;
  auctionId?: string;
  publicationId?: string;
  generation?: string;
  lotId?: string;
  documentId?: string;
  reviewedStep?: string;
}
export interface EventOperation extends EventScope {
  operationType: EventOperationType;
  idempotencyKey: string;
  startedAt: number;
}
export interface EventStore {
  readonly length: number;
  key(index: number): string | null;
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

export function eventBindingKey(binding: EventBinding) {
  const { audience, tenantId, accountId, supplierId, accessId } = binding;
  if (
    ![tenantId, accountId].every((id) => UUID.test(id)) ||
    (audience === "supplier" &&
      (!supplierId ||
        !accessId ||
        !UUID.test(supplierId) ||
        !UUID.test(accessId)))
  )
    throw new Error("Invalid event authority binding");
  return `wio-veyra.phase5.${audience}.v1:${tenantId}:${accountId}${audience === "supplier" ? `:${supplierId}:${accessId}` : ""}:`;
}

function project(value: unknown, binding: EventBinding): EventOperation {
  const row = value as Partial<EventOperation> | null;
  if (
    !row ||
    !UUID.test(row.eventId ?? "") ||
    !KEY.test(row.idempotencyKey ?? "") ||
    !EVENT_OPERATION_TYPES.includes(row.operationType as EventOperationType) ||
    (row.operationType?.startsWith("supplier.") ||
      row.operationType === "auction.rules.accept" ||
      row.operationType === "auction.step.accept" ||
      row.operationType === "auction.bid.submit" ||
      row.operationType?.startsWith("auction.document.") ||
      row.operationType === "auction.lot.bid.submit") !==
      (binding.audience === "supplier") ||
    (row.participationId !== undefined && !UUID.test(row.participationId)) ||
    (row.grantId !== undefined && !UUID.test(row.grantId)) ||
    (binding.audience === "supplier" &&
      (!row.participationId || !row.grantId)) ||
    typeof row.startedAt !== "number" ||
    !Number.isFinite(row.startedAt) ||
    row.startedAt < 0
  )
    throw new Error("Invalid event recovery record");
  const opening = row.operationType?.startsWith("opening.");
  const qualification = row.operationType === "qualification.record";
  const round = row.operationType === "sourcing.round.start";
  const auction =
    row.operationType?.startsWith("auction.document.") ||
    row.operationType === "auction.rules.accept" ||
    row.operationType === "auction.step.accept" ||
    row.operationType === "auction.bid.submit" ||
    row.operationType === "auction.lot.bid.submit";
  if (
    row.operationType === "auction.lot.bid.submit" &&
    !UUID.test(row.lotId ?? "")
  )
    throw new Error("Invalid auction lot recovery identity");
  if (
    row.operationType === "auction.document.freeze" &&
    !UUID.test(row.documentId ?? "")
  )
    throw new Error("Invalid auction document recovery identity");
  if (
    auction &&
    (!UUID.test(row.auctionId ?? "") ||
      !UUID.test(row.publicationId ?? "") ||
      !/^[1-9]\d{0,8}$/.test(row.generation ?? ""))
  )
    throw new Error("Invalid auction acceptance recovery identity");
  if (
    row.operationType === "auction.step.accept" &&
    !/^(0|[1-9]\d{0,3})$/.test(row.reviewedStep ?? "")
  )
    throw new Error("Invalid auction step recovery identity");
  if (
    round &&
    (!UUID.test(row.revisionId ?? "") ||
      !/^[1-9]\d{0,17}$/.test(row.expectedVersion ?? "") ||
      !/^[1-9]\d{0,8}$/.test(row.roundNumber ?? ""))
  )
    throw new Error("Invalid round recovery identity");
  if (
    (opening || qualification) &&
    (!UUID.test(row.revisionId ?? "") ||
      !/^[A-Za-z0-9_-]{1,40}$/.test(row.stageCode ?? "") ||
      (row.operationType === "opening.confirm" &&
        (!UUID.test(row.openingId ?? "") ||
          !/^[1-9]\d{0,17}$/.test(row.expectedVersion ?? ""))) ||
      (qualification && !/^(0|[1-9]\d{0,8})$/.test(row.expectedVersion ?? "")))
  )
    throw new Error("Invalid opening recovery identity");
  return {
    eventId: row.eventId!,
    participationId: row.participationId,
    grantId: row.grantId,
    operationType: row.operationType,
    idempotencyKey: row.idempotencyKey!,
    startedAt: row.startedAt,
    ...(auction
      ? {
          auctionId: row.auctionId,
          publicationId: row.publicationId,
          generation: row.generation,
          ...(row.operationType === "auction.step.accept"
            ? { reviewedStep: row.reviewedStep }
            : {}),
          ...(row.operationType === "auction.document.freeze"
            ? { documentId: row.documentId }
            : {}),
          ...(row.operationType === "auction.lot.bid.submit"
            ? { lotId: row.lotId }
            : {}),
        }
      : {}),
    ...(round
      ? {
          revisionId: row.revisionId,
          expectedVersion: row.expectedVersion,
          roundNumber: row.roundNumber,
        }
      : {}),
    ...(opening || qualification
      ? {
          revisionId: row.revisionId,
          stageCode: row.stageCode,
          ...(qualification ? { expectedVersion: row.expectedVersion } : {}),
          ...(row.operationType === "opening.confirm"
            ? { openingId: row.openingId, expectedVersion: row.expectedVersion }
            : {}),
        }
      : {}),
  } as EventOperation;
}

function openingPrefix(binding: EventBinding) {
  return eventBindingKey(binding).replace(
    "phase5.workspace.v1:",
    "opening-operations.v1:",
  );
}
function recordPrefix(binding: EventBinding, operation: EventOperation) {
  if (operation.operationType === "auction.step.accept")
    return auctionStepPrefix(binding);
  if (operation.operationType.startsWith("auction.document."))
    return auctionDocumentPrefix(binding);
  if (operation.operationType === "auction.lot.bid.submit")
    return auctionLotBidPrefix(binding);
  if (operation.operationType === "auction.bid.submit")
    return auctionBidPrefix(binding);
  if (operation.operationType === "auction.rules.accept")
    return auctionPrefix(binding);
  if (operation.operationType === "sourcing.round.start")
    return roundPrefix(binding);
  if (operation.operationType === "qualification.record")
    return qualificationPrefix(binding);
  return operation.operationType.startsWith("opening.")
    ? openingPrefix(binding)
    : eventBindingKey(binding);
}
function qualificationPrefix(binding: EventBinding) {
  return eventBindingKey(binding).replace(
    "phase5.workspace.v1:",
    "qualification-operations.v1:",
  );
}
function roundPrefix(binding: EventBinding) {
  return eventBindingKey(binding).replace(
    "phase5.workspace.v1:",
    "round-operations.v1:",
  );
}
function auctionPrefix(binding: EventBinding) {
  return eventBindingKey(binding).replace(
    "phase5.supplier.v1:",
    "auction-acceptance.v1:",
  );
}
export function ownsEventStorageKey(binding: EventBinding, key: string) {
  return [
    eventBindingKey(binding),
    openingPrefix(binding),
    qualificationPrefix(binding),
    roundPrefix(binding),
    auctionPrefix(binding),
    auctionBidPrefix(binding),
    auctionLotBidPrefix(binding),
    auctionDocumentPrefix(binding),
    auctionStepPrefix(binding),
  ].some((prefix) => key.startsWith(prefix));
}

function auctionBidPrefix(binding: EventBinding) {
  return eventBindingKey(binding).replace(
    "phase5.supplier.v1:",
    "auction-bid.v1:",
  );
}

function auctionStepPrefix(binding: EventBinding) {
  return eventBindingKey(binding).replace(
    "phase5.supplier.v1:",
    "auction-step.v1:",
  );
}

function auctionLotBidPrefix(binding: EventBinding) {
  return eventBindingKey(binding).replace(
    "phase5.supplier.v1:",
    "auction-lot-bid.v1:",
  );
}

function auctionDocumentPrefix(binding: EventBinding) {
  return eventBindingKey(binding).replace(
    "phase5.supplier.v1:",
    "auction-document.v1:",
  );
}

export function restoreEventOperations(
  store: EventStore,
  binding: EventBinding,
): EventOperation[] {
  const prefix = eventBindingKey(binding);
  const records: EventOperation[] = [];
  for (let i = 0; i < store.length; i++) {
    const key = store.key(i);
    if (
      !key?.startsWith(prefix) &&
      !key?.startsWith(openingPrefix(binding)) &&
      !key?.startsWith(qualificationPrefix(binding)) &&
      !key?.startsWith(roundPrefix(binding)) &&
      !key?.startsWith(auctionPrefix(binding)) &&
      !key?.startsWith(auctionBidPrefix(binding)) &&
      !key?.startsWith(auctionLotBidPrefix(binding)) &&
      !key?.startsWith(auctionStepPrefix(binding)) &&
      !key?.startsWith(auctionDocumentPrefix(binding))
    )
      continue;
    const record = project(JSON.parse(store.getItem(key) ?? "null"), binding);
    if (key !== recordPrefix(binding, record) + record.idempotencyKey)
      throw new Error("Mismatched event recovery key");
    records.push(record);
  }
  if (records.length > 12) throw new Error("Event recovery capacity exceeded");
  return records.sort((a, b) => a.startedAt - b.startedAt);
}

export function persistEventOperation(
  store: EventStore,
  binding: EventBinding,
  operation: EventOperation,
) {
  const record = project(operation, binding);
  const records = restoreEventOperations(store, binding);
  if (
    records.length >= 12 &&
    !records.some((item) => item.idempotencyKey === record.idempotencyKey)
  )
    throw new Error("Event recovery capacity exceeded");
  const key = recordPrefix(binding, record) + record.idempotencyKey;
  const encoded = JSON.stringify(record);
  store.setItem(key, encoded);
  if (store.getItem(key) !== encoded)
    throw new Error("Event recovery storage unavailable");
}

export function removeEventOperation(
  store: EventStore,
  binding: EventBinding,
  key: string,
) {
  if (!KEY.test(key)) throw new Error("Invalid event command key");
  store.removeItem(eventBindingKey(binding) + key);
  store.removeItem(openingPrefix(binding) + key);
  store.removeItem(qualificationPrefix(binding) + key);
  store.removeItem(roundPrefix(binding) + key);
  store.removeItem(auctionPrefix(binding) + key);
  store.removeItem(auctionBidPrefix(binding) + key);
  store.removeItem(auctionLotBidPrefix(binding) + key);
  store.removeItem(auctionDocumentPrefix(binding) + key);
  store.removeItem(auctionStepPrefix(binding) + key);
}

export function clearEventOperations(store: EventStore, binding: EventBinding) {
  const prefix = eventBindingKey(binding);
  const keys = Array.from({ length: store.length }, (_, index) =>
    store.key(index),
  );
  keys
    .filter((key): key is string =>
      Boolean(
        key?.startsWith(prefix) ||
        key?.startsWith(openingPrefix(binding)) ||
        key?.startsWith(qualificationPrefix(binding)) ||
        key?.startsWith(roundPrefix(binding)) ||
        key?.startsWith(auctionPrefix(binding)) ||
        key?.startsWith(auctionBidPrefix(binding)) ||
        key?.startsWith(auctionLotBidPrefix(binding)) ||
        key?.startsWith(auctionStepPrefix(binding)) ||
        key?.startsWith(auctionDocumentPrefix(binding)),
      ),
    )
    .forEach((key) => store.removeItem(key));
}

export function eventIntent(scope: EventScope, type: EventOperationType) {
  if (type === "auction.step.accept")
    return `${scope.eventId}:auction-step:${scope.publicationId}:${scope.generation}`;
  if (type.startsWith("auction.document."))
    return `${scope.eventId}:${type}:${scope.publicationId}:${scope.generation}:${type === "auction.document.freeze" ? scope.documentId : "prepare"}`;
  if (type === "auction.lot.bid.submit")
    return `${scope.eventId}:auction-lot-bid:${scope.publicationId}:${scope.lotId}:${scope.generation}`;
  if (type === "auction.bid.submit")
    return `${scope.eventId}:auction-bid:${scope.publicationId}:${scope.generation}`;
  if (type === "auction.rules.accept")
    return `${scope.eventId}:auction:${scope.publicationId}:${scope.generation}`;
  if (type === "qualification.record")
    return `${scope.eventId}:qualification:${scope.revisionId}:${scope.stageCode}`;
  if (type.startsWith("opening."))
    return `${scope.eventId}:opening:${scope.revisionId}:${scope.stageCode}`;
  const intent =
    type === "supplier.event.accept" || type === "supplier.event.decline"
      ? "supplier.event.decision"
      : type;
  return `${scope.eventId}:${scope.participationId ?? "event"}:${intent}`;
}

export function eventCompletion(value: unknown, operation: EventOperation) {
  if (!value || typeof value !== "object") return undefined;
  const row = value as Record<string, unknown>;
  if (operation.operationType === "auction.step.accept") {
    const field = (key: string) =>
      row[key] ?? row[key[0].toUpperCase() + key.slice(1)];
    const id = field("receiptId"),
      accepted = field("accepted"),
      sequence = field("acceptedSequence");
    const rejected = [
      "auction_not_started",
      "auction_closed",
      "auction_step_eliminated",
      "auction_step_changed",
      "auction_step_already_accepted",
      "auction_participant_unavailable",
    ];
    if (
      field("eventId") !== operation.eventId ||
      field("auctionId") !== operation.auctionId ||
      field("publicationId") !== operation.publicationId ||
      String(field("generation")) !== operation.generation ||
      String(field("reviewedStep")) !== operation.reviewedStep ||
      typeof id !== "string" ||
      !UUID.test(id) ||
      typeof accepted !== "boolean" ||
      (accepted
        ? field("code") !== "auction_step_accepted" ||
          !/^[1-9]\d{0,18}$/.test(String(sequence)) ||
          (typeof sequence === "number" && !Number.isSafeInteger(sequence))
        : sequence != null || !rejected.includes(String(field("code"))))
    )
      return undefined;
    return { eventId: operation.eventId, resourceId: id };
  }
  if (operation.operationType.startsWith("auction.document.")) {
    const field = (key: string) =>
      row[key] ?? row[key[0].toUpperCase() + key.slice(1)];
    const id = field("documentId");
    if (
      field("eventId") !== operation.eventId ||
      field("publicationId") !== operation.publicationId ||
      String(field("generation")) !== operation.generation ||
      typeof id !== "string" ||
      !UUID.test(id) ||
      (operation.operationType === "auction.document.freeze" &&
        id !== operation.documentId) ||
      field("state") !==
        (operation.operationType === "auction.document.prepare"
          ? "Pending"
          : "Ready")
    )
      return undefined;
    return { eventId: operation.eventId, resourceId: id };
  }
  if (
    operation.operationType === "auction.bid.submit" ||
    operation.operationType === "auction.lot.bid.submit"
  ) {
    const field = (key: string) =>
      row[key] ?? row[key[0].toUpperCase() + key.slice(1)];
    const id = field("receiptId"),
      accepted = field("accepted"),
      sequence = field("acceptedSequence"),
      code = field("code");
    const rejected = [
      "auction_not_started",
      "auction_closed",
      "auction_price_invalid",
      "auction_price_precision",
      "auction_improvement_required",
      "auction_equal_price",
      "auction_lot_incomplete",
      ...(operation.operationType === "auction.lot.bid.submit"
        ? [
            "auction_cost_incomplete",
            "auction_cost_invalid",
            "auction_currency_not_allowed",
          ]
        : []),
    ];
    if (
      field("eventId") !== operation.eventId ||
      field("auctionId") !== operation.auctionId ||
      field("publicationId") !== operation.publicationId ||
      String(field("generation")) !== operation.generation ||
      (operation.operationType === "auction.lot.bid.submit" &&
        (field("lotId") !== operation.lotId ||
          (accepted
            ? !/^[1-9]\d{0,18}$/.test(String(field("publicationSequence")))
            : field("publicationSequence") != null))) ||
      typeof id !== "string" ||
      !UUID.test(id) ||
      typeof accepted !== "boolean" ||
      (accepted
        ? code !== "auction_bid_accepted" ||
          !/^[1-9]\d{0,18}$/.test(String(sequence))
        : sequence != null ||
          typeof code !== "string" ||
          !rejected.includes(code))
    )
      return undefined;
    return { eventId: operation.eventId, resourceId: id };
  }
  if (operation.operationType === "auction.rules.accept") {
    const field = (key: string) =>
      row[key] ?? row[key[0].toUpperCase() + key.slice(1)];
    const id = field("acceptanceId");
    if (
      field("eventId") !== operation.eventId ||
      field("auctionId") !== operation.auctionId ||
      field("publicationId") !== operation.publicationId ||
      String(field("generation")) !== operation.generation ||
      typeof id !== "string" ||
      !UUID.test(id)
    )
      return undefined;
    return { eventId: operation.eventId, resourceId: id };
  }
  if (operation.operationType === "sourcing.round.start") {
    const field = (key: string) =>
      row[key] ?? row[key[0].toUpperCase() + key.slice(1)];
    const id = field("roundId"),
      version = String(field("version")),
      number = String(field("roundNumber"));
    if (
      field("eventId") !== operation.eventId ||
      field("previousRevisionId") !== operation.revisionId ||
      typeof id !== "string" ||
      !UUID.test(id) ||
      !/^[1-9]\d{0,17}$/.test(version) ||
      !/^[1-9]\d{0,9}$/.test(number) ||
      !operation.expectedVersion ||
      !operation.roundNumber ||
      BigInt(version) !== BigInt(operation.expectedVersion) + BigInt(1) ||
      BigInt(number) !== BigInt(operation.roundNumber) + BigInt(1)
    )
      return undefined;
    return { eventId: operation.eventId, resourceId: id };
  }
  if (operation.operationType === "qualification.record") {
    const field = (key: string) =>
      row[key] ?? row[key[0].toUpperCase() + key.slice(1)];
    const id = field("qualificationId"),
      version = String(field("version"));
    if (
      field("eventId") !== operation.eventId ||
      field("revisionId") !== operation.revisionId ||
      field("stageCode") !== operation.stageCode ||
      typeof id !== "string" ||
      !UUID.test(id) ||
      !/^[1-9]\d{0,9}$/.test(version) ||
      operation.expectedVersion === undefined ||
      BigInt(version) !== BigInt(operation.expectedVersion) + BigInt(1)
    )
      return undefined;
    return { eventId: operation.eventId, resourceId: id };
  }
  if (operation.operationType.startsWith("opening.")) {
    const field = (key: string) =>
      row[key] ?? row[key[0].toUpperCase() + key.slice(1)];
    const id = field("openingId"),
      version = String(field("version"));
    if (
      field("eventId") !== operation.eventId ||
      field("revisionId") !== operation.revisionId ||
      field("stageCode") !== operation.stageCode ||
      typeof id !== "string" ||
      !UUID.test(id) ||
      !/^[1-9]\d{0,17}$/.test(version)
    )
      return undefined;
    if (operation.operationType === "opening.request") {
      if (version !== "1" || field("openingReceiptId") != null)
        return undefined;
    } else {
      if (id !== operation.openingId || !operation.expectedVersion)
        return undefined;
      const delta = BigInt(version) - BigInt(operation.expectedVersion),
        receipt = field("openingReceiptId");
      if (
        receipt != null &&
        (typeof receipt !== "string" || !UUID.test(receipt))
      )
        return undefined;
      if (delta !== BigInt(1) && !(delta === BigInt(2) && receipt != null))
        return undefined;
    }
    return { eventId: operation.eventId, resourceId: id };
  }
  const eventId = row.eventId ?? row.EventId;
  const participationId = row.participationId ?? row.ParticipationId;
  const resourceId =
    row.resourceId ?? row.ResourceId ?? row.revisionId ?? row.RevisionId;
  if (
    eventId !== operation.eventId ||
    typeof resourceId !== "string" ||
    !UUID.test(resourceId) ||
    (operation.participationId && participationId !== operation.participationId)
  )
    return undefined;
  return { eventId: operation.eventId, resourceId };
}
